import { useEffect, useState } from "react";
import { useSiteStore } from "@/store/site";
import { cn } from "@/lib/cn";

/**
 * Tiny floating panel that surfaces the operator-spa's live wiring
 * state — toggle with `?debug=1` in the URL. Designed for the
 * "iframe goes black" / "live data not flowing" diagnosis flow:
 *
 *   - which adapter mode is selected (mock / live)
 *   - which adapter is actually wired (mock / TbAdapter)
 *   - the connection state machine (mock / connecting / live / error)
 *   - the last error message if connection failed
 *   - the live SiteSnapshot timestamp + lag from now
 *   - the discovered TB site (when live)
 *
 * Pinned to the bottom-right corner, dismissable with the × button.
 */
export function DebugOverlay() {
  const [enabled, setEnabled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const store = useSiteStore();

  useEffect(() => {
    if (typeof window === "undefined") return;
    setEnabled(new URLSearchParams(window.location.search).get("debug") === "1");
  }, []);

  if (!enabled || dismissed) return null;

  const ageMs = Date.now() - (store.snapshot?.ts ?? 0);
  const adapterName = (store.adapter as { constructor: { name: string } }).constructor.name;
  const topo = store.adapter.topology();
  // Feature-detected debug knob (MockAdapter only — not on the EmsAdapter
  // interface). Lets the entire stale/offline UI be exercised without a
  // live site. The overlay re-renders on every snapshot tick, so reading
  // outageActive straight off the adapter stays in sync.
  const outageHost = store.adapter as { setOutage?: (on: boolean) => void; outageActive?: boolean };
  const outageOn = outageHost.outageActive === true;
  const snapConn = store.snapshot?.connection ?? "live";

  const dot = (tone: "ok" | "warn" | "critical" | "muted") => ({
    ok:       "bg-ok",
    warn:     "bg-warn",
    critical: "bg-critical",
    muted:    "bg-text-muted",
  }[tone]);

  const connTone =
    store.connection === "live"      ? "ok"
    : store.connection === "connecting" ? "warn"
    : store.connection === "error"   ? "critical"
    : store.connection === "demo"    ? "ok"
    : "muted";

  return (
    <div className="fixed bottom-3 right-3 z-[120] max-w-sm rounded-lg border border-edge bg-surface/95 shadow-2xl backdrop-blur">
      <div className="flex items-center justify-between border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">
        <span className="flex items-center gap-1.5">
          <span className={cn("h-2 w-2 rounded-full", dot(connTone))} />
          operator-spa debug
        </span>
        <button onClick={() => setDismissed(true)} className="text-text-muted hover:text-text">×</button>
      </div>
      <div className="space-y-1.5 px-3 py-2 text-[11px]">
        <Row k="mode" v={store.mode} />
        <Row k="connection" v={store.connection} tone={connTone} />
        {store.connectionError && (
          <div className="rounded border border-critical/30 bg-critical/10 px-2 py-1 text-[10px] text-critical">
            {store.connectionError}
          </div>
        )}
        <Row k="adapter" v={adapterName} mono />
        <Row k="topology" v={`${topo?.name ?? "—"} (${topo?.bess?.length ?? 0}× BESS)`} />
        <Row k="topologyId" v={store.topologyId} mono small />
        <Row k="recent[]" v={`${store.recent.length} snapshots`} />
        <Row k="snap.ts" v={`${new Date(store.snapshot?.ts ?? 0).toLocaleTimeString()} (${(ageMs / 1000).toFixed(1)}s ago)`} />
        <Row k="snap.health" v={store.snapshot?.health?.overall ?? "—"} />
        {store.snapshot?.connection && <Row k="snap.conn" v={store.snapshot.connection} />}
        <Row k="iframed" v={String(typeof window !== "undefined" && window.parent !== window)} />
        <Row k="origin" v={typeof window !== "undefined" ? window.location.origin : "—"} mono small />
        {typeof outageHost.setOutage === "function" && (
          <div className="flex items-center justify-between gap-3 border-t border-edge pt-1.5">
            <span className="text-text-faint">Simulate telemetry outage</span>
            <span className="flex items-center gap-1.5">
              <span className={cn(
                "font-mono",
                snapConn === "live" && "text-ok",
                snapConn === "stale" && "text-stale",
                snapConn === "offline" && "text-critical",
              )}>{snapConn}</span>
              <button
                onClick={() => outageHost.setOutage?.(!outageOn)}
                className={cn(
                  "rounded border px-1.5 py-0.5 text-[10px] font-semibold",
                  outageOn
                    ? "border-warn/40 bg-warn/15 text-warn"
                    : "border-edge bg-surface-2 text-text-muted hover:text-text",
                )}
              >
                {outageOn ? "outage ON" : "outage OFF"}
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  k, v, mono, small, tone,
}: { k: string; v: string; mono?: boolean; small?: boolean; tone?: "ok" | "warn" | "critical" | "muted" }) {
  const toneClass = tone === "ok" ? "text-ok"
    : tone === "warn" ? "text-warn"
    : tone === "critical" ? "text-critical"
    : "text-text";
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-text-faint">{k}</span>
      <span className={`${mono ? "font-mono" : ""} ${small ? "text-[10px]" : ""} ${toneClass}`}>{v}</span>
    </div>
  );
}
